import React, { useState, useRef, useEffect } from "react";
import {
  FiSend,
  FiX,
  FiFile,
  FiMessageCircle,
  FiChevronDown,
  FiCopy,
  FiCheck,
  FiPaperclip,
  FiUpload,
  FiPlus,
  FiMinus,
} from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import { fetchWithAuth } from "../../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const AssessmentInsights = () => {
  const [messages, setMessages] = useState([]);
  const [questions, setQuestions] = useState([""]);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedAnswers, setExpandedAnswers] = useState({});
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRefs = useRef([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Auto-resize textareas
    textareaRefs.current.forEach((textarea) => {
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
      }
    });
  }, [questions]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFiles = (newFiles) => {
    const validFiles = newFiles.filter((file) => {
      const isValidType = file.type === "application/pdf";
      const isValidSize = file.size <= 20 * 1024 * 1024;
      return isValidType && isValidSize;
    });

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, ""]);
    // Focus the new textarea after it's rendered
    setTimeout(() => {
      const newIndex = questions.length;
      if (textareaRefs.current[newIndex]) {
        textareaRefs.current[newIndex].focus();
      }
    }, 0);
  };

  const removeQuestion = (index) => {
    if (questions.length > 1) {
      setQuestions((prev) => prev.filter((_, i) => i !== index));
      textareaRefs.current = textareaRefs.current.filter((_, i) => i !== index);
    }
  };

  const updateQuestion = (index, value) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? value : q)));
  };

  const handleSubmit = async () => {
    const validQuestions = questions.filter((q) => q.trim());
    if (validQuestions.length === 0 || files.length === 0) {
      return;
    }

    // Add questions to history before sending
    setQuestionHistory((prev) => [...prev, ...validQuestions]);
    setHistoryIndex(-1); // Reset history index

    const userMessage = {
      id: Date.now(),
      type: "user",
      questions: validQuestions,
      files: files.map((f) => f.name),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("contextFiles", file);
      });
      formData.append("questions", JSON.stringify(validQuestions));

      const response = await fetchWithAuth(
        `${serverUrl}/api/document-query/answer?tasks=assessment-insights`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const botMessage = {
        id: Date.now() + 1,
        type: "bot",
        answers: data.answers || [],
        contextFiles: data.contextFilesUsed || [],
        totalQuestions: data.totalQuestions,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        answers: validQuestions.map((question, index) => ({
          questionNumber: (index + 1).toString(),
          question: question,
          answer:
            "Sorry, there was an error processing your request. Please try again.",
          source: null,
          topic: null,
          pageNumbers: null,
        })),
        isError: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
      // Reset to initial state: single empty question
      setQuestions([""]);
      // Reset textarea refs to match the single question
      textareaRefs.current = [textareaRefs.current[0]];
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "ArrowUp") {
      // Up Arrow: Navigate through question history
      e.preventDefault();
      if (questionHistory.length > 0) {
        const newHistoryIndex =
          historyIndex === -1
            ? questionHistory.length - 1
            : Math.max(0, historyIndex - 1);

        setHistoryIndex(newHistoryIndex);
        updateQuestion(index, questionHistory[newHistoryIndex]);
      }
    } else if (e.key === "ArrowDown") {
      // Down Arrow: Navigate forward through question history
      e.preventDefault();
      if (historyIndex > -1) {
        const newHistoryIndex = historyIndex + 1;
        if (newHistoryIndex >= questionHistory.length) {
          setHistoryIndex(-1);
          updateQuestion(index, "");
        } else {
          setHistoryIndex(newHistoryIndex);
          updateQuestion(index, questionHistory[newHistoryIndex]);
        }
      }
    } else if (e.key === "Enter" && e.shiftKey) {
      // Shift + Enter: Add new question
      e.preventDefault();
      addQuestion();
      setHistoryIndex(-1); // Reset history index when adding new question
    } else if (e.key === "Enter" && !e.shiftKey) {
      // Enter: Send questions
      e.preventDefault();
      handleSubmit();
    } else {
      // Reset history index when user starts typing
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
        setHistoryIndex(-1);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return "📄";
      case "txt":
        return "📝";
      case "doc":
      case "docx":
        return "📋";
      default:
        return "📄";
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const toggleAnswerExpansion = (messageId, answerIndex) => {
    const key = `${messageId}-${answerIndex}`;
    setExpandedAnswers((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const canSubmit = () => {
    const validQuestions = questions.filter((q) => q.trim());
    return validQuestions.length > 0 && files.length > 0 && !isLoading;
  };

  return (
    <div className="doc-chat-modern-container">
      {/* Messages Area */}
      <div className="doc-chat-modern-messages">
        {messages.length === 0 ? (
          <div className="doc-chat-modern-empty">
            <div className="empty-animation">
              <div className="floating-docs">
                <div className="doc-icon doc-1">📄</div>
                <div className="doc-icon doc-2">📋</div>
                <div className="doc-icon doc-3">📝</div>
              </div>
              <div className="empty-content">
                <h3>Ready to analyze your assessments</h3>
                <p>
                  {files.length > 0
                    ? "Upload more files from below or ask questions to get started"
                    : "Upload files and ask multiple questions to unlock intelligent insights"}
                </p>
                {files.length <= 0 && (
                  <div
                    className="upload-cta"
                    onClick={() => setShowFileUploadModal(true)}
                  >
                    <button className="add-question-btn">
                      <FiPaperclip />
                      Upload Files
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message-wrapper ${message.type}`}
              >
                <div className="message-avatar">
                  <div className={`avatar-icon ${message.type}`}>
                    {message.type === "user" ? (
                      <img
                        src="/imgs/person_icon.svg"
                        alt="User Avatar"
                        width={24}
                        height={24}
                      />
                    ) : (
                      <img
                        src="/imgs/logo-cropped.svg"
                        alt="AI Avatar"
                        width={24}
                        height={24}
                      />
                    )}
                  </div>
                </div>

                <div className="message-content-wrapper">
                  <div className="message-content">
                    <div className="message-header">
                      <span className="message-sender">
                        {message.type === "user" ? "You" : "GradeMe AI"}
                      </span>
                    </div>

                    <div
                      className={`message-body ${
                        message.isError ? "error" : ""
                      }`}
                    >
                      {message.answers ? (
                        <div className="answers-container">
                          {message.answers.map((answer, answerIndex) => {
                            const isExpanded =
                              expandedAnswers[`${message.id}-${answerIndex}`];
                            const isLongAnswer = answer.answer.length > 300;

                            return (
                              <div key={answerIndex} className="answer-card">
                                <div className="answer-header">
                                  <div className="question-badge">
                                    Q{answer.questionNumber}
                                  </div>
                                  <h4 className="question-text">
                                    {answer.question}
                                  </h4>
                                  <button
                                    onClick={() =>
                                      copyToClipboard(
                                        answer.answer,
                                        `${message.id}-${answerIndex}`
                                      )
                                    }
                                    className="copy-button"
                                    title="Copy answer"
                                  >
                                    {copiedIndex ===
                                    `${message.id}-${answerIndex}` ? (
                                      <FiCheck />
                                    ) : (
                                      <FiCopy />
                                    )}
                                  </button>
                                </div>

                                <div className="answer-content">
                                  <div
                                    className={`answer-text ${
                                      isLongAnswer && !isExpanded
                                        ? "truncated"
                                        : ""
                                    }`}
                                  >
                                    <ReactMarkdown>
                                      {answer.answer}
                                    </ReactMarkdown>
                                  </div>

                                  {isLongAnswer && (
                                    <button
                                      onClick={() =>
                                        toggleAnswerExpansion(
                                          message.id,
                                          answerIndex
                                        )
                                      }
                                      className="expand-toggle"
                                    >
                                      <span>
                                        {isExpanded ? "Show less" : "Show more"}
                                      </span>
                                      <FiChevronDown
                                        className={isExpanded ? "rotated" : ""}
                                      />
                                    </button>
                                  )}
                                </div>

                                {answer.source && (
                                  <div className="answer-metadata">
                                    <div className="source-info">
                                      <FiFile className="source-icon" />
                                      <span className="source-text">
                                        I found this answer in {answer.source}{" "}
                                        {answer.pageNumbers &&
                                          `on page ${answer.pageNumbers}`}
                                        {answer.topic &&
                                          `, under the topic "${answer.topic}"`}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="user-questions-container">
                          {message.questions?.map((question, index) => (
                            <div key={index} className="user-question-item">
                              <div className="question-header">
                                <div className="question-number">
                                  Q{index + 1}
                                </div>
                                <div className="question-content">
                                  {question}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {message.contextFiles &&
                        message.contextFiles.length > 0 && (
                          <div className="context-files">
                            <div className="context-label">
                              <FiFile /> Analyzed documents:
                            </div>
                            <div className="context-list">
                              {message.contextFiles.map((fileName, index) => (
                                <span key={index} className="context-badge">
                                  {fileName}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="message-timestamp">
                    <span className="message-time">
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="message-wrapper bot loading">
                <div className="message-avatar">
                  <div className="avatar-icon bot">
                    <img
                      src="/imgs/logo-cropped.svg"
                      alt="AI Avatar"
                      width={24}
                      height={24}
                    />
                  </div>
                </div>
                <div className="message-content-wrapper">
                  <div className="message-content">
                    <div className="message-header">
                      <span className="message-sender">GradeMe AI</span>
                    </div>
                    <div className="message-body">
                      <div className="typing-indicator">
                        <div className="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="typing-text">
                          Analyzing your documents...
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="message-timestamp">
                    <span className="message-time">
                      {formatTime(new Date())}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="doc-chat-modern-input">
        <div className="questions-input-section">
          <div className="questions-list">
            {questions.map((question, index) => (
              <div key={index} className="question-input-wrapper">
                <div className="question-input-container">
                  <span className="question-label">Q{index + 1}:</span>
                  <textarea
                    ref={(el) => (textareaRefs.current[index] = el)}
                    value={question}
                    onChange={(e) => updateQuestion(index, e.target.value)}
                    placeholder={`Enter question ${index + 1}...`}
                    className="question-textarea"
                    rows={1}
                    disabled={isLoading}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                  />
                  {questions.length > 1 && (
                    <button
                      onClick={() => removeQuestion(index)}
                      className="remove-question-btn"
                      title="Remove question"
                    >
                      <FiMinus />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="input-hint">
            <span>
              <FiMessageCircle style={{ marginRight: "0.5rem" }} />
              Press Shift+Enter to add new question • Press Enter to send
            </span>
            <div className="input-actions">
              <button
                onClick={() => setShowFileUploadModal(true)}
                className="add-question-btn"
                title="Upload files"
              >
                <FiPaperclip />
                {files.length > 0 && (
                  <span className="file-count-badge">{files.length}</span>
                )}
                Add Files
              </button>
              <button
                onClick={addQuestion}
                className="add-question-btn"
                disabled={isLoading}
              >
                <FiPlus /> Add Question
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit()}
                className="send-button"
              >
                {isLoading ? (
                  <div className="button-spinner">
                    <div className="spinner-ring"></div>
                  </div>
                ) : (
                  <>
                    <FiSend /> Send Questions
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* File Upload Modal */}
      {showFileUploadModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowFileUploadModal(false)}
        >
          <div
            className="file-upload-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Upload Documents</h3>
              <button
                onClick={() => setShowFileUploadModal(false)}
                className="modal-close-btn"
              >
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div
                className={`file-upload-zone ${
                  dragActive ? "drag-active" : ""
                } ${files.length > 0 ? "has-files" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf"
                  onChange={(e) =>
                    handleFiles(Array.from(e.target.files || []))
                  }
                  style={{ display: "none" }}
                />

                {files.length === 0 ? (
                  <div className="upload-prompt">
                    <div className="upload-icon-wrapper">
                      <FiUpload className="upload-icon" />
                      <div className="upload-ripple"></div>
                    </div>
                    <div className="upload-content">
                      <h4>Drop your documents here</h4>
                      <p>
                        or{" "}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="browse-link"
                        >
                          browse files
                        </button>
                      </p>
                      <div className="upload-info">
                        <span className="supported-formats">PDF only</span>
                        <span className="size-limit">Max 20MB each</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="files-preview">
                    <div className="files-header">
                      <div className="files-count">
                        <FiFile />
                        <span>
                          {files.length} file{files.length > 1 ? "s" : ""} ready
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="add-more-btn"
                      >
                        <FiUpload /> Add more
                      </button>
                    </div>
                    <div className="files-grid">
                      {files.map((file, index) => (
                        <div key={index} className="file-preview-card">
                          <div className="file-icon">
                            {getFileIcon(file.name)}
                          </div>
                          <div className="file-details">
                            <div className="file-name" title={file.name}>
                              {file.name}
                            </div>
                            <div className="file-size">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="remove-file-btn"
                          >
                            <FiX />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowFileUploadModal(false)}
                className="modal-done-btn"
              >
                Done ({files.length} files)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentInsights;
